import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  InputAdornment,
} from '@mui/material';

// Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';

// Custom components
import StockSearchBar from '../../../common/StockSearchBar';

const InsiderTradeConfig = ({ alertData, onUpdateAlertData }) => {
  // Get the main filters and conditions from alert data
  const mainFilters = useMemo(() => alertData.mainFilters || [], [alertData.mainFilters]);
  const conditions = useMemo(() => alertData.conditions || {}, [alertData.conditions]);

  // Extract symbols from main filters
  const symbols = mainFilters
    .filter(filter => filter.type === 'symbol')
    .map(filter => filter.value);

  // Handle adding a symbol to the alert
  const handleSymbolSelect = useCallback((symbol, resultData) => {
    if (!symbol) return;

    // Check if symbol already exists
    if (symbols.includes(symbol)) {
      return;
    }

    // Update main filters with the new symbol
    const updatedMainFilters = [
      ...mainFilters,
      { type: 'symbol', value: symbol }
    ];

    onUpdateAlertData({
      mainFilters: updatedMainFilters
    });
  }, [mainFilters, symbols, onUpdateAlertData]);

  // Handle removing a symbol
  const handleRemoveSymbol = useCallback((symbolToRemove) => {
    const updatedMainFilters = mainFilters.filter(
      filter => !(filter.type === 'symbol' && filter.value === symbolToRemove)
    );

    onUpdateAlertData({
      mainFilters: updatedMainFilters
    });
  }, [mainFilters, onUpdateAlertData]);

  // Handle min amount change
  const handleMinAmountChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        minAmount: e.target.value
      }
    });
  };

  // Handle min shares change
  const handleMinSharesChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        minShares: e.target.value
      }
    });
  };

  // Handle transaction type change
  const handleTransactionTypeChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        transactionType: e.target.value
      }
    });
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        1. Select Stocks to Monitor
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <StockSearchBar
            onSymbolSelect={handleSymbolSelect}
            currentSymbols={symbols}
            maxSymbols={10}
            placeholder="Search for stocks by symbol"
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {symbols.length > 0 ? (
            symbols.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                onDelete={() => handleRemoveSymbol(symbol)}
                color="primary"
                variant="outlined"
                icon={<ShowChartIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="error">
              Please select at least one stock to monitor insider trading activity.
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        2. Additional Filters (Optional)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Minimum Transaction Amount"
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { min: 0 } // Set minimum value to 0
              }}
              fullWidth
              value={conditions.minAmount || ''}
              onChange={(e) => {
                // Ensure the value is not negative
                const value = parseFloat(e.target.value);
                if (value < 0 || isNaN(value)) {
                  // If negative or not a number, set to 0
                  handleMinAmountChange({ target: { value: 0 } });
                } else {
                  handleMinAmountChange(e);
                }
              }}
              helperText="Only notify for transactions above this amount"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Minimum Shares"
              type="number"
              InputProps={{
                inputProps: { min: 0 } // Set minimum value to 0
              }}
              fullWidth
              value={conditions.minShares || ''}
              onChange={(e) => {
                // Ensure the value is not negative
                const value = parseFloat(e.target.value);
                if (value < 0 || isNaN(value)) {
                  // If negative or not a number, set to 0
                  handleMinSharesChange({ target: { value: 0 } });
                } else {
                  handleMinSharesChange(e);
                }
              }}
              helperText="Only notify for transactions with more than this many shares"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
              <Select
                labelId="transaction-type-label"
                value={conditions.transactionType || ''}
                onChange={handleTransactionTypeChange}
                label="Transaction Type"
              >
                <MenuItem value="">All Transaction Types</MenuItem>
                <MenuItem value="Purchase">Purchases Only</MenuItem>
                <MenuItem value="Sale">Sales Only</MenuItem>
                <MenuItem value="Option Exercise">Option Exercises</MenuItem>
                <MenuItem value="Grant/Award">Grants & Awards</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default InsiderTradeConfig;